import { useEffect, useRef } from 'react';
import { useUIStore, type DynamicTab, type SortItem } from '../stores/uiStore';
import { rpcCall } from '../api/rpc';
import { useRoentgenStore } from '../features/roentgen/roentgenStore';

const STATE_KEY = 'uiState';
const DEBOUNCE_MS = 2000;

/** Shape of persisted UI state */
interface PersistedUIState {
  activeTab: string;
  dynamicTabs: DynamicTab[];
  detailPanelOpen: boolean;
  aggregationPanelOpen: boolean;
  quickSearchField: string;
  sortModel?: SortItem[];
  roentgenDrawerOpen?: boolean;
  roentgenAllowUiActions?: boolean;
}

/** Build the persistence payload from the current store snapshot */
function buildPayload(): PersistedUIState {
  const s = useUIStore.getState();
  return {
    activeTab: s.activeTab,
    dynamicTabs: s.dynamicTabs.map((t) => ({
      id: t.id,
      label: t.label,
      type: t.type,
      adminPanel: t.adminPanel,
      closeable: t.closeable,
      // Viewer tabs need the study object to rebuild the OHIF URL on restore.
      // Study is a plain interface (strings/numbers) — fully JSON-serializable.
      ...(t.type === 'viewer' && t.study ? { study: t.study } : {}),
    })),
    detailPanelOpen: s.detailPanelOpen,
    aggregationPanelOpen: s.aggregationPanelOpen,
    quickSearchField: s.quickSearchField,
    sortModel: s.sortModel,
    roentgenDrawerOpen: useRoentgenStore.getState().drawerOpen,
    roentgenAllowUiActions: useRoentgenStore.getState().allowUiActions,
  };
}

/**
 * Immediately persist the current UI state to the backend.
 * Call this before destroying the session (e.g. on logout)
 * so that any pending debounced save is not lost.
 */
export async function flushStatePersistence(): Promise<void> {
  try {
    await rpcCall('setjson', { key: STATE_KEY, value: buildPayload() });
  } catch {
    // best-effort
  }
}

/**
 * Persists UI state (active tab, open tabs, panel visibility, etc.)
 * across sessions via the getjson/setjson JSON-RPC endpoints.
 *
 * Call this once inside AppShell after the user is authenticated.
 *
 * On mount: loads saved state and applies it to uiStore.
 * On change: debounce-saves state to the backend.
 */
export function useStatePersistence() {
  const restoredRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Restore saved state on mount (once)
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;

    (async () => {
      try {
        const result = await rpcCall<PersistedUIState | null>('getjson', {
          key: STATE_KEY,
        });
        if (result != null && typeof result === 'object') {
          const state = result as PersistedUIState;
          const store = useUIStore.getState();

          // Restore dynamic tabs (merge with defaults — avoid duplicates)
          if (Array.isArray(state.dynamicTabs)) {
            const existingIds = new Set(store.dynamicTabs.map((t) => t.id));
            const merged = [...store.dynamicTabs];
            for (const tab of state.dynamicTabs) {
              if (!existingIds.has(tab.id)) {
                merged.push(tab);
                existingIds.add(tab.id);
              }
            }
            useUIStore.setState({ dynamicTabs: merged });
          }

          // Restore active tab
          if (state.activeTab) {
            useUIStore.setState({ activeTab: state.activeTab });
          }

          // Restore panel visibility
          if (typeof state.detailPanelOpen === 'boolean') {
            useUIStore.setState({ detailPanelOpen: state.detailPanelOpen });
          }
          if (typeof state.aggregationPanelOpen === 'boolean') {
            useUIStore.setState({ aggregationPanelOpen: state.aggregationPanelOpen });
          }
          if (state.quickSearchField) {
            useUIStore.setState({ quickSearchField: state.quickSearchField });
          }
          if (Array.isArray(state.sortModel)) {
            useUIStore.setState({ sortModel: state.sortModel });
          }
          if (typeof state.roentgenDrawerOpen === 'boolean') {
            useRoentgenStore.setState({ drawerOpen: state.roentgenDrawerOpen });
          }
          if (typeof state.roentgenAllowUiActions === 'boolean') {
            useRoentgenStore.setState({ allowUiActions: state.roentgenAllowUiActions });
          }
        }
      } catch {
        // No saved state or error — keep defaults
      }
    })();
  }, []);

  // Subscribe to store changes and debounce-save
  useEffect(() => {
    const schedulePersist = () => {
      // Clear any pending save
      if (timerRef.current) clearTimeout(timerRef.current);

      timerRef.current = setTimeout(() => {
        rpcCall('setjson', { key: STATE_KEY, value: buildPayload() }).catch(() => {
          // Silently ignore save failures
        });
      }, DEBOUNCE_MS);
    };

    const unsubUI = useUIStore.subscribe(schedulePersist);
    const unsubRoentgen = useRoentgenStore.subscribe(schedulePersist);

    return () => {
      unsubUI();
      unsubRoentgen();
      // Flush on unmount — save whatever is pending right now
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        rpcCall('setjson', { key: STATE_KEY, value: buildPayload() }).catch(() => {});
      }
    };
  }, []);
}
